<template>
  <div class="login-container">
    <div class="login-card">
      <!-- Logo and Title -->
      <div class="login-header">
        <div class="">
          <img src="@/assets/images/logo-cesetti.png" alt="Logo">
        </div>
        <h1 class="login-title">MyGeotab Feeder</h1>
        <p class="login-subtitle">Sistema de Gestión Vehicular</p>
      </div>

      <!-- Login Form -->
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="email" class="form-label">
            <i class="bi bi-envelope me-2"></i>
            Correo Electrónico
          </label>
          <input
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email }"
            id="email"
            v-model="formData.email"
            placeholder="usuario@ejemplo.com"
            autocomplete="email"
            required
            autofocus
          >
          <div class="invalid-feedback" v-if="errors.email">
            {{ errors.email }}
          </div>
        </div>

        <div class="form-group">
          <label for="password" class="form-label">
            <i class="bi bi-lock me-2"></i>
            Contraseña
          </label>
          <div class="password-input-group">
            <input
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              :class="{ 'is-invalid': errors.password }"
              id="password"
              v-model="formData.password"
              placeholder="••••••••"
              autocomplete="current-password"
              required
            >
            <button
              type="button"
              class="password-toggle"
              @click="togglePassword"
              :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
            >
              <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
          <div class="invalid-feedback" v-if="errors.password" style="display: block;">
            {{ errors.password }}
          </div>
        </div>

        <div class="form-options">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="rememberMe"
              v-model="formData.rememberMe"
            >
            <label class="form-check-label" for="rememberMe">
              Recordarme
            </label>
          </div>
          <a href="#" class="forgot-password" @click.prevent="handleForgotPassword">
            ¿Olvidaste tu contraseña?
          </a>
        </div>

        <button type="submit" class="btn btn-primary btn-login" :disabled="authStore.loading">
          <span v-if="authStore.loading">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Iniciando sesión...
          </span>
          <span v-else>
            <i class="bi bi-box-arrow-in-right me-2"></i>
            Iniciar Sesión
          </span>
        </button>
      </form>

      <!-- Error Message -->
      <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ errorMessage }}
      </div>

      <!-- Footer -->
      <div class="login-footer">
        <p class="text-muted">
          <i class="bi bi-shield-check me-1"></i>
          Conexión segura y encriptada
        </p>
      </div>
    </div>

    <!-- Background Animation -->
    <div class="background-animation">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const router = useRouter()
const authStore = useAuthStore()

// Form data
const formData = reactive({
  email: '',
  password: '',
  rememberMe: false
})

const errors = ref({})
const showPassword = ref(false)
const errorMessage = ref('')

// Methods
const togglePassword = () => {
  showPassword.value = !showPassword.value
}

const validateForm = () => {
  errors.value = {}
  errorMessage.value = ''

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!formData.email) {
    errors.value.email = 'El correo electrónico es requerido'
  } else if (!emailRegex.test(formData.email)) {
    errors.value.email = 'Ingrese un correo electrónico válido'
  }

  // Password validation
  if (!formData.password) {
    errors.value.password = 'La contraseña es requerida'
  } else if (formData.password.length < 6) {
    errors.value.password = 'La contraseña debe tener al menos 6 caracteres'
  }

  return Object.keys(errors.value).length === 0
}

const handleLogin = async () => {
  if (!validateForm()) {
    return
  }

  errorMessage.value = ''

  try {
    // Call auth store login method
    await authStore.login(formData.email, formData.password, formData.rememberMe)

    // Redirigir según el tipo de usuario
    if (authStore.user?.client_id) {
      // Usuario asociado a un cliente - redirigir a la página de detalle del cliente
      router.push({
        name: 'ClientDetail',
        params: { id: authStore.user.client_id }
      })
    } else {
      // Usuario administrador - redirigir al dashboard
      router.push('/')
    }
  } catch (error) {
    console.error('Login error:', error)
    errorMessage.value = error.message || 'Credenciales inválidas. Por favor, intente nuevamente.'
  }
}

const handleForgotPassword = () => {
  // TODO: Implementar lógica de recuperación de contraseña
  console.log('Forgot password clicked')
  alert('Funcionalidad de recuperación de contraseña próximamente...')
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #fff 50%, #764ba2 100%);
}

.login-card {
  background: linear-gradient(135deg, #fff 100% #764ba2 100%);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 3rem;
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 10;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.login-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.logo-circle {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 15px 40px rgba(102, 126, 234, 0.6);
  }
}

.login-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.login-subtitle {
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin: 0;
}

.login-form {
  margin-top: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.9rem;
}

.form-control {
  background: var(--input-bg);
  border: 2px solid var(--border-color);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.form-control:focus {
  background: var(--input-bg);
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
  color: var(--text-primary);
}

.form-control::placeholder {
  color: var(--text-secondary);
  opacity: 0.6;
}

.password-input-group {
  position: relative;
}

.password-input-group .form-control {
  padding-right: 3rem;
}

.password-toggle {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 3rem;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.password-toggle:hover {
  color: var(--text-primary);
}

.password-toggle:focus {
  outline: none;
  color: #667eea;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-check-label {
  color: var(--text-primary);
  font-size: 0.9rem;
}

.form-check-input {
  background-color: var(--input-bg);
  border-color: var(--border-color);
}

.form-check-input:checked {
  background-color: #667eea;
  border-color: #667eea;
}

.forgot-password {
  color: #667eea;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.3s ease;
}

.forgot-password:hover {
  color: #764ba2;
  text-decoration: underline;
}

.btn-login {
  width: 100%;
  padding: 0.875rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-login:active:not(:disabled) {
  transform: translateY(0);
}

.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
}

.login-footer p {
  margin: 0;
  font-size: 0.85rem;
}

.alert {
  border-radius: 10px;
  border: none;
  font-size: 0.9rem;
}

/* Background Animation */
.background-animation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  animation: float 20s ease-in-out infinite;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.circle-2 {
  width: 400px;
  height: 400px;
  bottom: -150px;
  right: -150px;
  animation-delay: 7s;
}

.circle-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 14s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.3;
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
    opacity: 0.5;
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
    opacity: 0.4;
  }
}

/* Dark theme adjustments */
:root[data-theme="dark"] .login-container {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

:root[data-theme="dark"] .login-card {
  background: rgba(26, 26, 46, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Responsive Design */
@media (max-width: 576px) {
  .login-card {
    padding: 2rem 1.5rem;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .logo-circle {
    width: 70px;
    height: 70px;
    font-size: 1.75rem;
  }

  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .circle-1, .circle-2, .circle-3 {
    display: none;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .logo-circle,
  .circle,
  .btn-login {
    animation: none;
  }

  .btn-login:hover:not(:disabled) {
    transform: none;
  }
}
</style>
